import { memo } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import rehypeRaw from "rehype-raw";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { ArrowClockwiseIcon } from "@phosphor-icons/react";
import styles from "@/zapi-frontend/shared/ui/custom/ChatMessage/ChatMessage.module.css";
import {
  AiMessage,
  BulletBox,
  ErrorBox,
  NoteBox,
  TypingDots,
  UserMessage
} from "@/zapi-frontend/shared/ui/chat";
import { parseMessageContent } from "../lib/parseMessageContent";
import { PresentationChatMessage as TPresentationChatMessage } from "../hooks/usePresentationChat";

type TPresentationChatMessageProps = {
  message: TPresentationChatMessage;
  isLoading?: boolean;
  onActionButton?: (messageText: string, messageId: string) => void;
};

const markdownComponents: Components = {
  a: ({ node: _node, ...props }) => {
    const isInternal =
      props.href?.startsWith("/") || props.href?.includes("platform.innovera") || props.href?.includes("localhost");
    return (
      // eslint-disable-next-line jsx-a11y/anchor-has-content
      <a
        {...props}
        {...(isInternal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
        className="underline text-[#3367E5]"
      />
    );
  },
  p: ({ node: _node, ...props }) => <p {...props} className="mb-2 last:mb-0" />,
  ul: ({ node: _node, ...props }) => <ul {...props} className="list-disc pl-5 mb-2 last:mb-0" />,
  ol: ({ node: _node, ...props }) => <ol {...props} className="list-decimal pl-5 mb-2 last:mb-0" />,
  li: ({ node: _node, ...props }) => <li {...props} className="mb-1" />,
  h1: ({ node: _node, color: _color, children, ...props }) => <FigmaTypography component="h1" variant="memo-heading-2" {...props} className="mb-2">{children}</FigmaTypography>,
  h2: ({ node: _node, color: _color, children, ...props }) => <FigmaTypography component="h2" variant="memo-heading-3" {...props} className="mb-2">{children}</FigmaTypography>,
  h3: ({ node: _node, color: _color, children, ...props }) => <FigmaTypography component="h3" variant="memo-body-semibold" {...props} className="mb-2">{children}</FigmaTypography>,
  strong: ({ node: _node, ...props }) => <strong {...props} className="font-semibold" />
};

export const PresentationChatMessage = memo(function PresentationChatMessage(props: TPresentationChatMessageProps) {
  const { message, isLoading, onActionButton } = props;

  if (message.role === "user") {
    return (
      <div className={`${styles.animatefadeIn} w-full`}>
        <UserMessage className="w-full">
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
        </UserMessage>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`${styles.animatefadeIn} w-full`}>
        <AiMessage className="w-full">
          <TypingDots />
        </AiMessage>
      </div>
    );
  }

  const parsedContent = parseMessageContent(message.content);

  const renderContent = () => {
    switch (parsedContent.type) {
      case "error":
        return (
          <AiMessage className="w-full" state="error">
            <ErrorBox>{parsedContent.content}</ErrorBox>
          </AiMessage>
        );
      case "warning":
        return (
          <AiMessage className="w-full">
            <NoteBox>{parsedContent.content}</NoteBox>
          </AiMessage>
        );
      case "notification": {
        const hasChanges = parsedContent.changes && parsedContent.changes.length > 0;
        return (
          <AiMessage className="w-full" state={hasChanges && !message.isActedOn ? "error" : "default"}>
            {parsedContent.content && (
              <FigmaTypography variant="body-2" color="zinc900" className="w-full">
                {parsedContent.content}
              </FigmaTypography>
            )}
            {hasChanges && (
              <BulletBox tone={message.isActedOn ? "gray" : "red"} items={parsedContent.changes ?? []} />
            )}
            {!message.isActedOn && parsedContent.question && (
              <FigmaTypography variant="body-2" color="zinc900" className="w-full">
                {parsedContent.question}
              </FigmaTypography>
            )}
            {!message.isActedOn && parsedContent.actionButtons && parsedContent.actionButtons.length > 0 && (
              <div className="flex items-start gap-[8px]">
                {parsedContent.actionButtons.map((btn, i) => (
                  <AppButton
                    key={i}
                    size="sm"
                    color={btn.appearance === "contained" ? "zinc950" : "zinc150"}
                    appearance={btn.appearance ?? "outlined"}
                    type="button"
                    onClick={() => onActionButton?.(btn.message, message.id)}
                    startAdornment={btn.action === "submit" ? <ArrowClockwiseIcon size={12} /> : undefined}
                  >
                    {btn.label}
                  </AppButton>
                ))}
              </div>
            )}
          </AiMessage>
        );
      }
      case "answer":
      default:
        return (
          <AiMessage className="w-full">
            <div className="w-full text-[14px] leading-[22px] text-innoveraZinc-900">
              <ReactMarkdown rehypePlugins={[rehypeRaw]} components={markdownComponents}>
                {parsedContent.content || ""}
              </ReactMarkdown>
            </div>
          </AiMessage>
        );
    }
  };

  return <div className={`${styles.animatefadeIn} w-full`}>{renderContent()}</div>;
});
